import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import axios from "axios";

const LabelModal = (props) => {
  const { open, getStatus, compName, compData, pracData, currentUserEmail } =
    props;

  const [show, setShow] = useState(open);
  const [data, setData] = useState(null);

  // In practice mode we pass the logged-in user's email; when set, we
  // hide the user's own historical rows from the table so they don't
  // see their old live-queue labels alongside their current practice answer.
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return data;
    if (!pracData || !currentUserEmail) return data;
    const me = String(currentUserEmail).toLowerCase();
    return data.filter(
      (d) => !d || !d.email || String(d.email).toLowerCase() !== me
    );
  }, [data, pracData, currentUserEmail]);

  // When the user clicks "Show Results" outside the auto-fetch path
  // (e.g., re-opening the modal after dismissing it). Re-uses the
  // already-fetched `data` if present, otherwise mirrors the effect
  // below's fetch logic. Does not re-issue the network call.
  const componentData = () => {
    if (!data && !getStatus) {
      setData(compData);
    }
    setShow(true);
  };

  // Reset cached labels whenever the IC we're rendering for changes.
  // Without this, after submitting IC-A and then submitting IC-B, the
  // modal would render IC-B's title against IC-A's table data until
  // the new fetch resolved.
  useEffect(() => {
    setData(null);
  }, [compName]);

  // Async fetch the IC's label history when in `getStatus` mode. The
  // closure captures `fetchedFor`; if `compName` changes mid-flight,
  // the response is dropped to avoid the "title says IC-B / table
  // shows IC-A" race that used to be possible here.
  useEffect(() => {
    if (!getStatus || !compName) return;
    let cancelled = false;
    const fetchedFor = compName;
    axios
      .get("/api/components/component", { params: { name: compName } })
      .then((response) => {
        if (cancelled || fetchedFor !== compName) return;
        setData(response.data?.labels || []);
        setShow(true);
      })
      .catch(() => {
        if (!cancelled) setShow(true);
      });
    return () => {
      cancelled = true;
    };
  }, [compName, getStatus]);

  return (
    <div>
      <Button
        variant="primary"
        onClick={componentData}
        style={{ marginRight: "10px" }}
      >
        Show Results
      </Button>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {(() => {
              const base = compName.substring(0, compName.lastIndexOf("."));
              // Match the IC label baked into the image: prefix with "IC"
              // unless the filename already has it.
              return base.toUpperCase().startsWith("IC") ? `${base} Results` : `IC${base} Results`;
            })()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {filteredData ? (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Labeler</th>
                  <th>Brain</th>
                  <th>Muscle</th>
                  <th>Eye</th>
                  <th>Heart</th>
                  <th>Line Noise</th>
                  <th>Channel Noise</th>
                  <th>Other</th>
                  <th>?</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((datapoint, idx) => {
                  const fullName = `${datapoint.fname || ""} ${
                    datapoint.lname || ""
                  }`.trim();
                  const displayName =
                    fullName ||
                    (datapoint.email
                      ? datapoint.email.split("@")[0]
                      : datapoint.anonId
                      ? `Labeler ${datapoint.anonId}`
                      : datapoint.domain || "user");
                  return (
                    <tr key={`${datapoint.email || datapoint.anonId || "unknown"}-${idx}`}>
                      <th>{displayName}</th>
                      <th>{datapoint.tags.includes("Brain") ? "✓" : ""}</th>
                      <th>{datapoint.tags.includes("Muscle") ? "✓" : ""}</th>
                      <th>{datapoint.tags.includes("Eye") ? "✓" : ""}</th>
                      <th>{datapoint.tags.includes("Heart") ? "✓" : ""}</th>
                      <th>
                        {datapoint.tags.includes("Line Noise") ? "✓" : ""}
                      </th>
                      <th>
                        {datapoint.tags.includes("Channel Noise") ? "✓" : ""}
                      </th>
                      <th>{datapoint.tags.includes("Other") ? "✓" : ""}</th>
                      <th>{datapoint.tags.includes("Unsure") ? "✓" : ""}</th>
                    </tr>
                  );
                })}
                {pracData && (
                  <tr key="practice" style={{ backgroundColor: "#C2EABD" }}>
                    <th>Your practice answer</th>
                    <th>{pracData.includes("Brain") ? "✓" : ""}</th>
                    <th>{pracData.includes("Muscle") ? "✓" : ""}</th>
                    <th>{pracData.includes("Eye") ? "✓" : ""}</th>
                    <th>{pracData.includes("Heart") ? "✓" : ""}</th>
                    <th>{pracData.includes("Line Noise") ? "✓" : ""}</th>
                    <th>{pracData.includes("Channel Noise") ? "✓" : ""}</th>
                    <th>{pracData.includes("Other") ? "✓" : ""}</th>
                    <th>{pracData.includes("Unsure") ? "✓" : ""}</th>
                  </tr>
                )}
              </tbody>
            </Table>
          ) : (
            <div></div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShow(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabelModal;
